import dayjs from 'dayjs';

/**
 * Utility functions for standardized date handling across financial reports.
 * Ensures consistent 'YYYY-MM-DD' formatting for backend compatibility
 * and enforces month-end boundary snapping.
 */

export const BACKEND_DATE_FORMAT = 'YYYY-MM-DD';
export const DISPLAY_DATE_FORMAT = 'DD-MM-YYYY';

/**
 * Snaps a given date to the end of its month.
 * @param {dayjs.Dayjs|string} date 
 * @returns {string} Formatted YYYY-MM-DD
 */
export const snapToEndOfMonth = (date) => {
    if (!date) return '';
    return dayjs(date).endOf('month').format(BACKEND_DATE_FORMAT);
};

/**
 * Validates and adjusts an end date relative to a start date.
 * If the end date is before the start date, it snaps to the end of the start date's month.
 * @param {dayjs.Dayjs|string} newEndDate - The proposed end date
 * @param {dayjs.Dayjs|string} startDate - The current start date
 * @returns {string} Final formatted end date
 */
export const getAdjustedEndDate = (newEndDate, startDate) => {
    if (!newEndDate) return '';
    
    const end = dayjs(newEndDate);
    const start = dayjs(startDate);
    
    if (startDate && end.isBefore(start, 'day')) {
        return snapToEndOfMonth(start);
    }
    
    return end.format(BACKEND_DATE_FORMAT);
};

/**
 * Validates and adjusts a start date relative to an end date.
 * If the start date is after the end date, it provides a suggested end date snapped to month-end.
 * @param {dayjs.Dayjs|string} newStartDate - The proposed start date
 * @param {dayjs.Dayjs|string} currentEndDate - The current end date
 * @returns {Object} { startDate: string, suggestedEndDate: string|null }
 */
export const getAdjustedStartDate = (newStartDate, currentEndDate) => {
    if (!newStartDate) return { startDate: '', suggestedEndDate: null };
    
    const start = dayjs(newStartDate);
    const end = dayjs(currentEndDate);
    const startDateStr = start.format(BACKEND_DATE_FORMAT);
    
    let suggestedEndDate = null;
    if (currentEndDate && start.isAfter(end, 'day')) {
        suggestedEndDate = snapToEndOfMonth(start);
    }
    
    return { startDate: startDateStr, suggestedEndDate };
};
